/**
 * Absolute Value generator (6.NR.2.3).
 *
 * Absolute value is the distance of a number from zero, so it is never
 * negative. The inner value is chosen first and the answer is computed with
 * Rational.abs(), keeping display and answer in agreement. One family compares
 * two absolute values (choice); the rest ask for a single absolute value or a
 * distance from zero (number).
 *
 * Families:
 *   F1: absolute value of a negative integer      (number)
 *   F2: absolute value of a positive integer       (number)
 *   F3: absolute value of a fraction               (number)
 *   F4: which absolute value is greater             (choice)
 */

import { rat, type Rational } from "../engine/rational";
import { Rng } from "../engine/rng";
import { getTopic } from "../curriculum/curriculum";
import type {
  Choice,
  Difficulty,
  GenerateContext,
  Generator,
  Question,
  QuestionSignature,
} from "./types";

const TOPIC = "absolute-value" as const;
const VERSION = "1.0.0";

type Family = "F1" | "F2" | "F3" | "F4";

interface Cfg {
  readonly families: readonly Family[];
  readonly max: number;
  readonly denoms: readonly number[];
}

const DIFF: Record<Difficulty, Cfg> = {
  easy: { families: ["F1", "F2"], max: 12, denoms: [2, 4, 5, 10] },
  standard: { families: ["F1", "F2", "F3", "F4"], max: 25, denoms: [2, 4, 5, 8, 10] },
  hard: { families: ["F1", "F3", "F4"], max: 60, denoms: [4, 8, 20, 25] },
  challenge: { families: ["F3", "F4"], max: 120, denoms: [8, 20, 25, 50] },
};

export class AbsoluteValueGenerator implements Generator {
  readonly topic = TOPIC;
  readonly version = VERSION;
  readonly supportedDifficulties: readonly Difficulty[] = [
    "easy",
    "standard",
    "hard",
    "challenge",
  ];

  generate(ctx: GenerateContext): Question {
    const topic = getTopic(TOPIC);
    for (let attempt = 0; attempt < 100; attempt++) {
      const rng = new Rng(`${ctx.seed}#${attempt}`);
      const cfg = DIFF[ctx.difficulty];
      const family = rng.pick(cfg.families);

      if (family === "F4") {
        // Compare two absolute values; choose the one with the greater magnitude.
        const a = this.signedValue(rng, cfg, true);
        let b = this.signedValue(rng, cfg, true);
        // Ensure distinct magnitudes so a strict "greater" exists.
        let guard = 0;
        while (a.abs().equals(b.abs()) && guard < 50) {
          b = this.signedValue(rng, cfg, true);
          guard++;
        }
        if (a.abs().equals(b.abs())) continue; // couldn't separate; retry seed

        const aGreater = a.abs().gt(b.abs());
        const correctId = aGreater ? "a" : "b";
        const choices: Choice[] = [
          { id: "a", latex: `\\left|${a.toLatex()}\\right|` },
          { id: "b", latex: `\\left|${b.toLatex()}\\right|` },
        ];
        return {
          id: `${TOPIC}-${ctx.seed}-${family}`,
          generator: TOPIC,
          generatorVersion: VERSION,
          seed: ctx.seed,
          unit: topic.unit,
          topic: TOPIC,
          family,
          difficulty: ctx.difficulty,
          prompt: {
            text: `Which is greater: |${a.toString()}| or |${b.toString()}|?`,
            latex: `\\left|${a.toLatex()}\\right| \\;?\\; \\left|${b.toLatex()}\\right|`,
          },
          answerType: "choice",
          answer: { kind: "choice", value: correctId },
          steps: [
            {
              description:
                "Absolute value is distance from zero, so take each value's distance and compare those.",
              latex: `\\left|${a.toLatex()}\\right| = ${a.abs().toLatex()},\\; \\left|${b.toLatex()}\\right| = ${b.abs().toLatex()}`,
            },
          ],
          hints: [
            { level: 1, text: "Absolute value ignores the sign; it is only the distance from zero." },
            { level: 2, text: "Compare the sizes of the two values, not their signs." },
          ],
          subskills: topic.subskills,
          choices,
        };
      }

      // Number families: |value| = answer.
      let value: Rational;
      let stepDesc: string;
      switch (family) {
        case "F1": {
          value = rat(-rng.int(1, cfg.max));
          stepDesc = "The absolute value of a negative number is its positive distance from zero.";
          break;
        }
        case "F2": {
          value = rat(rng.int(1, cfg.max));
          stepDesc = "The absolute value of a positive number is the number itself.";
          break;
        }
        case "F3": {
          const den = rng.pick(cfg.denoms);
          const num = rng.int(1, den + Math.floor(den / 2));
          value = rat(num, den).mul(rat(rng.sign(0.6)));
          stepDesc = "Absolute value is distance from zero, so drop the sign.";
          break;
        }
      }

      if (value.isZero()) continue;
      const answer = value.abs();

      return {
        id: `${TOPIC}-${ctx.seed}-${family}`,
        generator: TOPIC,
        generatorVersion: VERSION,
        seed: ctx.seed,
        unit: topic.unit,
        topic: TOPIC,
        family,
        difficulty: ctx.difficulty,
        prompt: {
          text: `What is |${value.toString()}|?`,
          latex: `\\left|${value.toLatex()}\\right|`,
        },
        answerType: "number",
        answer: { kind: "number", value: answer },
        steps: [
          {
            description: stepDesc,
            latex: `\\left|${value.toLatex()}\\right| = ${answer.toLatex()}`,
          },
        ],
        hints: [
          { level: 1, text: "Absolute value is how far the number is from zero." },
          { level: 2, text: "Distance is never negative, so the answer is positive (or zero)." },
        ],
        subskills: topic.subskills,
      };
    }
    throw new Error(`${TOPIC}: failed after 100 attempts (${ctx.seed}, ${ctx.difficulty})`);
  }

  /** A signed integer or fraction value used by the comparison family. */
  private signedValue(rng: Rng, cfg: Cfg, allowFraction: boolean): Rational {
    if (allowFraction && rng.bool(0.4)) {
      const den = rng.pick(cfg.denoms);
      const num = rng.int(1, den + Math.floor(den / 2));
      return rat(num, den).mul(rat(rng.sign(0.6)));
    }
    return rat(rng.int(1, cfg.max) * rng.sign(0.6));
  }

  signature(q: Question): QuestionSignature {
    const structure =
      q.answer.kind === "choice"
        ? q.answer.value
        : q.answer.kind === "number"
          ? q.answer.value.key()
          : q.answer.kind;
    return { generator: TOPIC, family: q.family, difficulty: q.difficulty, structure };
  }
}

export const absoluteValue = new AbsoluteValueGenerator();
